
import { mapActions, mapGetters, mapState } from 'vuex'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mdiArrowRight, mdiCalendar, mdiHandPointingRight, mdiRvTruck, mdiTruckTrailer } from '@mdi/js'
import eav from '@/components/tools/eav'
import LoadingContainer from '@/components/ui/LoadingContainer.vue'
import time from '@/components/tools/time'
import LandingMyAdItem from '@/components/landing/LandingMyAdItem.vue'
import utils from '@/components/tools/utils'
import constants from '@/components/tools/constants'
import tracking from '@/components/tools/tracking'

export default {
  components: {
    LoadingContainer,
    ValidationObserver,
    ValidationProvider,
    LandingMyAdItem
  },
  props: {
    createNew: {
      type: Boolean,
      default: false
    },

    section: {
      type: Object,
      required: true
    },
    source: {
      type: String,
      default: undefined
    }
  },
  data () {
    return {
      mdiHandPointingRight,
      mdiRvTruck,
      mdiTruckTrailer,
      mdiCalendar,
      mdiArrowRight,
      loading: false
    }
  },
  async fetch () {
    await Promise.all([
      this.createNew ? this.getEmptyProduct() : this.getOnboardingStepperData({}),
      this.getAttributes(),
      this.getAttributesDe(),
      this.getProductTypeOptionIds()])
  },

  computed: {
    ...mapState('customer', ['merchant']),
    ...mapGetters({ isNlDomain: 'isNlDomain' }),
    isMerchant () {
      return typeof this.merchant?.merchantId === 'number'
    },

    ...mapState('upload', {
      product: state => state.product,
      attributes: state => state.attributes,
      attributesDe: state => state.attributesDe,
      productTypeOptionIds: state => state.productTypeOptionIds

    }),
    entityId () {
      return this.product.entityId
    },
    status () {
      return this.product?.auction?.status
    },
    showStepper () {
      return this.product?.entityId === null
    },

    plannedDateOfSaleDirectsIntoVerticalStepper () {
      if (!this.plannedDateOfSaleValue?.optionId) {
        return false
      }

      if (
        this.productTypeValue &&
        this.productTypeValue?.optionId === this.productTypeOptionIds.motorhome &&
        this.plannedDateOfSaleValue?.optionId
      ) {
        const optionIds = this.attributesDe?.planned_date_of_sale?.options
          .filter(
            option =>
              option?.label.toLowerCase().includes('nicht') ||
              option?.label.toLowerCase().includes('monaten') ||
              option?.label.toLowerCase().includes('kein')
          )
          .map(option => option.optionId)
        return optionIds.includes(this.plannedDateOfSaleValue?.optionId)
      }

      return false
    },

    ctaText () {
      return this.section.ctaText
    },

    pending () {
      return this.$fetchState.pending
    },
    disabled () {
      return false
    },

    productTypeValue: {
      get () {
        return eav.getEavAttribute(
          'product_type',
          this.product.attributes,
          this.attributes
        )
      },
      set (value) {
        this.$store.commit('upload/setAttribute', {
          attributeCode: 'product_type',
          value
        })
      }
    },

    productTypeLabel () {
      return this.attributes.product_type?.label
    },
    productTypeItems () {
      if (this.section.disabledProductType) {
        const that = this
        return this.attributes.product_type?.options.map((item) => {
          if (that.productTypeOptionIds.trailer === item.optionId && that.section.disabledProductType === 'trailer') {
            item.disabled = true
          } else if (that.productTypeOptionIds.motorhome === item.optionId && that.section.disabledProductType === 'motorhome') {
            item.disabled = true
          }

          return item
        })
      }

      return this.attributes.product_type?.options
    },
    isTrailer () {
      return (
        this.productTypeValue?.optionId === this.productTypeOptionIds.trailer
      )
    },
    makeSelect () {
      if (this.isTrailer) {
        return {
          attributeCode: 'trailer_make',
          icon: mdiTruckTrailer
        }
      }

      return {
        attributeCode: 'make',
        icon: mdiRvTruck
      }
    },

    makeValue: {
      get () {
        return eav.getEavAttribute(
          this.makeSelect.attributeCode,
          this.product.attributes,
          this.attributes
        )
      },
      set (value) {
        this.$store.commit('upload/setAttribute', {
          attributeCode: this.makeSelect.attributeCode,
          value
        })
      }
    },

    plannedDateOfSaleValue: {
      get () {
        return eav.getEavAttribute(
          'planned_date_of_sale',
          this.product.attributes,
          this.attributes
        )
      },
      set (value) {
        this.$store.commit('upload/setAttribute', {
          attributeCode: 'planned_date_of_sale',
          value
        })
      }
    },
    makeItems () {
      if (this.isTrailer) {
        return this.attributes[this.makeSelect.attributeCode]?.options
          .filter(option =>
            !utils.isTrailerMakeBlacklisted(option.label))
      } else {
        return this.attributes[this.makeSelect.attributeCode]?.options
          .filter(option =>
            !utils.isMakeBlacklisted(option.label))
      }
    },

    plannedDateOfSaleLabel () {
      return this.attributes.planned_date_of_sale?.label
    },
    plannedDateOfSaleItems () {
      const transOptionId = this.attributesDe?.planned_date_of_sale?.options
        .filter(
          option =>
            option?.label.toLowerCase().includes('trans')
        )
        .map(option => option.optionId)
      const merchantOptionsIds = this.attributesDe?.planned_date_of_sale?.options
        .filter(
          option =>
            option?.label.toLowerCase().includes('sofort') ||
            option?.label.toLowerCase().includes('zeitnah')
        )
        .map(option => option.optionId)

      if (this.isMerchant) {
        return this.attributes.planned_date_of_sale?.options.filter(option =>
          merchantOptionsIds.includes(option.optionId) &&
          !transOptionId.includes(option.optionId)
        )
      }

      if (
        this.productTypeValue &&
        this.productTypeValue?.optionId === this.productTypeOptionIds.trailer
      ) {
        const trailerOptionsIds = this.attributesDe?.planned_date_of_sale?.options
          .filter(
            option =>
              option?.label.toLowerCase().includes('sofort') ||
              option?.label.toLowerCase().includes('zeitnah') ||
              option?.label.toLowerCase().includes('wochen')
          )
          .map(option => option.optionId)

        return this.attributes.planned_date_of_sale?.options.filter(option =>
          trailerOptionsIds.includes(option.optionId) &&
          !transOptionId.includes(option.optionId)
        )
      } else {
        return this.attributes.planned_date_of_sale?.options.filter(option =>
          !transOptionId.includes(option.optionId)
        )
      }
    }

  },
  mounted () {
  },

  methods: {
    ...mapActions('general', ['showSnackbar']),
    ...mapActions('customer', ['getAds']),
    ...mapActions('upload', [
      'getOnboardingStepperData',
      'getProduct',
      'getEmptyProduct',
      'getProductTypeOptionIds',
      'getAttributes',
      'getAttributesDe',
      'createProduct',
      'updateProduct'
    ]),

    async getAdsFromApi () {
      const page = 1

      this.loading = true
      await this.getAds(page)
    },

    endingAt (product) {
      return time.parseDate(product.auction?.endingAt)
    },

    async justGoToAdIntake () {
      const productType = this.product.productType
      await this.$router.push(
        this.localePath({
          name: `ad-edit-${productType}-profile-id`,
          params: { id: this.product.entityId }
        })
      )
    },

    async submitAndGoToAdIntake () {
      if (this.entityId) {
        const {
          status,
          data
        } = await this.updateProduct({ entityId: this.entityId })

        if (status !== 200) {
          await this.showSnackbar({
            text: this.$t('error'),
            color: 'error'
          })
          return
        }
        const productType = data.productType
        await this.$router.push(
          this.localePath({
            name: `ad-edit-${productType}-profile-id`,
            params: { id: this.product.entityId }
          })
        )
      } else {
        const {
          status,
          data
        } = await this.createProduct({
          silent: true,
          source: tracking.getSource(this.source, this.$route),
          sourceUrl: this.$route.path,
          funnel: constants.TP.FUNNEL.AD_INTAKE
        })
        if (status !== 200) {
          await this.showSnackbar({
            text: this.$t('error'),
            color: 'error'
          })
          return
        }
        const productType = data.productType
        await this.$router.push(
          this.localePath({
            name: `ad-edit-${productType}-profile-id`,
            params: { id: data.entityId }
          })
        )
      }
    },

    getVerticalStepperRoute () {
      const result = ({
        de: '/inserat/schritt-1',
        en: '/advertisement/step-1',
        nl: '/advertentie/stap-1'
      })[this.$i18n.locale] ?? '/inserat/schritt-1'
      return result
    },

    async submitAndGoToVerticalStepper () {
      let productId
      let auctionId
      if (this.entityId) {
        const { status } = await this.updateProduct({ entityId: this.entityId })

        if (status !== 200) {
          await this.showSnackbar({
            text: this.$t('error'),
            color: 'error'
          })
          return
        }
        this.$cookies.set('funnel_20_unsubmitted', this.entityId)
        await this.$router.push(this.getVerticalStepperRoute())
        productId = this.entityId
        auctionId = this.product?.auction.auctionId
      } else {
        await this.$store.commit('upload/setAttribute', {
          attributeCode: 'entered_vstepper',
          value: new Date()
        })

        const {
          status,
          data
        } = await this.createProduct({
          silent: true,
          source: tracking.getSource(this.source, this.$route),
          sourceUrl: this.$route.path,
          funnel: constants.TP.FUNNEL.VSTEPPER
        })
        if (status !== 200) {
          await this.showSnackbar({
            text: this.$t('error'),
            color: 'error'
          })
          return
        }
        this.$cookies.set('funnel_20_unsubmitted', data.entityId)
        await this.$router.push(this.getVerticalStepperRoute())
        productId = data.entityId
        auctionId = data.auction?.auctionId
      }

      try {
        this.$gtm.push({
          event: 'enter_vstepper',
          auctionId,
          productId,
          currency: 'EUR',
          transaction_id: auctionId + '_enter_vstepper'
        })
      } catch (e) {
        console.error(e)
      }
    },

    async submit () {
      this.loading = true

      if (this.createNew) {
        if (this.plannedDateOfSaleDirectsIntoVerticalStepper) {
          await this.submitAndGoToVerticalStepper()
        } else {
          await this.submitAndGoToAdIntake()
        }
        this.loading = false
        return
      }

      if (this.plannedDateOfSaleDirectsIntoVerticalStepper) {
        await this.submitAndGoToVerticalStepper()
      } else {
        await this.submitAndGoToAdIntake()
      }

      this.$refs.observer?.reset()
      this.loading = false
    }

  }
}
